import React, { useState, useEffect } from 'react';
import { inventoryAPI } from '../services/api';
import { isAdmin, isEncargada } from '../hooks/useSucursal';
import './LotesModal.css';

const LotesModal = ({ product, sucursalId, sucursalNombre, onClose, onStockUpdated, showToast }) => {
    const [lotes, setLotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('list'); // 'list' | 'new'

    // Formulario de nueva recepción de lote
    const [newLote, setNewLote] = useState({
        numero_lote: '',
        fecha_vencimiento: '',
        cantidad: '',
        ubicacion: '',
        observacion: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Estado para edición de info (metadatos)
    const [isEditingInfoId, setIsEditingInfoId] = useState(null);
    const [editInfoForm, setEditInfoForm] = useState({ numero_lote: '', fecha_vencimiento: '' });
    const [isSavingInfo, setIsSavingInfo] = useState(false);

    // Estado para ajuste
    const [adjustingLoteId, setAdjustingLoteId] = useState(null);
    const [adjustForm, setAdjustForm] = useState({ cantidad: '', motivo: '', observacion: '' });
    const [isAdjusting, setIsAdjusting] = useState(false);

    // Permisos
    const canAdjust = isAdmin() || isEncargada();

    useEffect(() => {
        if (product?.id) {
            loadLotes();
        }
    }, [product, sucursalId]);

    const loadLotes = async () => {
        setIsLoading(true);
        try {
            const res = await inventoryAPI.getLotes({
                producto_id: product.id,
                sucursal_id: sucursalId || undefined
            });
            setLotes(res.data?.data?.lotes || []);
        } catch (error) {
            console.error('Error al cargar lotes:', error);
            if (showToast) showToast('Error al cargar los lotes del producto', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const getExpiryStatus = (fechaVencimiento) => {
        if (!fechaVencimiento) return { label: 'Sin fecha registrada', class: 'gray', days: null, icon: '⚪' };

        // Normalizar: pg devuelve DATE como ISO string con timezone ('2028-12-31T00:00:00.000Z')
        // Tomamos solo los primeros 10 chars para obtener 'YYYY-MM-DD' limpio.
        const fechaStr = String(fechaVencimiento).substring(0, 10);

        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const venc = new Date(fechaStr + 'T00:00:00');

        if (isNaN(venc.getTime())) {
            return { label: 'Sin fecha registrada', class: 'gray', days: null, icon: '⚪' };
        }

        const diffMs = venc - hoy;
        const diffDias = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (diffDias < 0) {
            return { label: `VENCIDO hace ${Math.abs(diffDias)} días`, class: 'expired', days: diffDias, icon: '⚫' };
        }
        if (diffDias <= 30) {
            return { label: `CRÍTICO (${diffDias} días restantes)`, class: 'critical', days: diffDias, icon: '🔴' };
        }
        if (diffDias <= 90) {
            return { label: `Por vencer (${diffDias} días)`, class: 'warning', days: diffDias, icon: '🟡' };
        }
        return { label: `Vigente (${diffDias} días)`, class: 'valid', days: diffDias, icon: '🟢' };
    };

    const handleCreateLote = async (e) => {
        e.preventDefault();
        const cant = parseFloat(newLote.cantidad);

        if (!newLote.numero_lote.trim()) {
            if (showToast) showToast('El número de lote es obligatorio', 'warning');
            return;
        }
        if (!newLote.fecha_vencimiento) {
            if (showToast) showToast('La fecha de vencimiento es obligatoria', 'warning');
            return;
        }
        if (isNaN(cant) || cant <= 0) {
            if (showToast) showToast('La cantidad debe ser mayor a 0', 'warning');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                producto_id: product.id,
                sucursal_id: sucursalId || null,
                numero_lote: newLote.numero_lote.trim().toUpperCase(),
                fecha_vencimiento: newLote.fecha_vencimiento,
                cantidad: cant,
                ubicacion: newLote.ubicacion.trim() || undefined,
                observacion: newLote.observacion.trim() || undefined
            };

            await inventoryAPI.createLote(payload);
            if (showToast) showToast('✅ Lote y stock ingresados exitosamente', 'success');

            // Resetear form y recargar
            setNewLote({
                numero_lote: '',
                fecha_vencimiento: '',
                cantidad: '',
                ubicacion: '',
                observacion: ''
            });
            setActiveTab('list');
            await loadLotes();
            if (onStockUpdated) onStockUpdated();
        } catch (error) {
            console.error('Error al registrar lote:', error);
            const msg = error.response?.data?.message || error.message || 'Error al registrar el lote';
            if (showToast) showToast(`❌ ${msg}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAdjustLote = async (e, id) => {
        e.preventDefault();
        const delta = parseFloat(adjustForm.cantidad);
        if (isNaN(delta) || delta < 0) {
            if (showToast) showToast('La nueva cantidad debe ser 0 o mayor', 'warning');
            return;
        }
        if (!adjustForm.motivo) {
            if (showToast) showToast('Debes seleccionar un motivo', 'warning');
            return;
        }

        setIsAdjusting(true);
        try {
            await inventoryAPI.adjustLote(id, {
                nueva_cantidad: delta,
                motivo: adjustForm.motivo,
                observacion: adjustForm.observacion
            });
            if (showToast) showToast('Stock de lote ajustado correctamente', 'success');
            setAdjustingLoteId(null);
            loadLotes();
            if (onStockUpdated) onStockUpdated();
        } catch (error) {
            console.error('Error al ajustar lote:', error);
            if (showToast) showToast(error.response?.data?.message || 'Error al ajustar el lote', 'error');
        } finally {
            setIsAdjusting(false);
        }
    };

    const handleSaveInfo = async (id) => {
        if (!editInfoForm.numero_lote.trim()) {
            if (showToast) showToast('El número de lote no puede estar vacío', 'warning');
            return;
        }
        if (!editInfoForm.fecha_vencimiento) {
            if (showToast) showToast('La fecha de vencimiento es obligatoria', 'warning');
            return;
        }

        setIsSavingInfo(true);
        try {
            await inventoryAPI.updateLoteInfo(id, editInfoForm);
            if (showToast) showToast('Información del lote actualizada', 'success');
            setIsEditingInfoId(null);
            loadLotes();
        } catch (error) {
            console.error('Error al editar información del lote:', error);
            if (showToast) showToast(error.response?.data?.message || 'Error al actualizar lote', 'error');
        } finally {
            setIsSavingInfo(false);
        }
    };

    const totalLotesStock = lotes.reduce((sum, l) => sum + parseFloat(l.cantidad_actual || 0), 0);

    return (
        <div className="lotes-modal-overlay" onClick={onClose}>
            <div className="lotes-modal-card" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="lotes-modal-header">
                    <div className="lotes-header-title">
                        <span className="lotes-pill-badge">💊 Control Farmacia & FEFO</span>
                        <h2>{product.name}</h2>
                        <div className="lotes-header-meta">
                            {product.code && <span className="meta-tag">SKU: {product.code}</span>}
                            {product.presentacion && <span className="meta-tag presentacion">🧴 {product.presentacion}</span>}
                            {sucursalNombre && <span className="meta-tag sucursal">🏪 {sucursalNombre}</span>}
                        </div>
                    </div>
                    <button className="lotes-close-btn" onClick={onClose}>&times;</button>
                </div>

                {/* Tabs */}
                <div className="lotes-tabs">
                    <button 
                        className={`lotes-tab-btn ${activeTab === 'list' ? 'active' : ''}`}
                        onClick={() => setActiveTab('list')}
                    >
                        📋 Lotes Activos ({lotes.filter(l => parseFloat(l.cantidad_actual) > 0).length})
                    </button>
                    {lotes.length === 0 && (
                        <button 
                            className={`lotes-tab-btn ${activeTab === 'new' ? 'active' : ''}`}
                            onClick={() => setActiveTab('new')}
                        >
                            📥 Recepcionar / Ingresar Lote
                        </button>
                    )}
                </div>

                {/* Resumen Superior */}
                <div className="lotes-summary-bar">
                    <div className="summary-item">
                        <span className="summary-label">Stock en Lotes</span>
                        <strong className="summary-val">{totalLotesStock.toFixed(2)} {product.unit || 'uds'}</strong>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Orden Dispensación</span>
                        <strong className="summary-val fefo">⚡ FEFO (Vencimiento + Próximo)</strong>
                    </div>
                </div>

                {/* Contenido según Tab */}
                <div className="lotes-modal-body">
                    {activeTab === 'list' && (
                        <>
                            {isLoading ? (
                                <div className="lotes-loading">
                                    <div className="spinner"></div>
                                    <p>Cargando trazabilidad de lotes...</p>
                                </div>
                            ) : lotes.length === 0 ? (
                                <div className="lotes-empty">
                                    <span className="empty-icon">📦</span>
                                    <h4>No hay lotes registrados para este medicamento</h4>
                                    <p>Haz clic en "Recepcionar / Ingresar Lote" para cargar las existencias con su fecha de vencimiento.</p>
                                    <button 
                                        className="btn-lote-primary"
                                        onClick={() => setActiveTab('new')}
                                    >
                                        + Ingresar Primer Lote
                                    </button>
                                </div>
                            ) : (
                                <div className="lotes-table-container">
                                    <table className="lotes-table">
                                        <thead>
                                            <tr>
                                                <th>N° Lote</th>
                                                <th>Vencimiento</th>
                                                <th>Estado (Semáforo)</th>
                                                <th>Stock Actual</th>
                                                <th>Ubicación</th>
                                                <th>Sucursal</th>
                                                {canAdjust && <th>Acciones</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lotes.map((lote) => {
                                                const status = getExpiryStatus(lote.fecha_vencimiento);
                                                const stockNum = parseFloat(lote.cantidad_actual);
                                                const isDepleted = stockNum === 0;
                                                const isEditingInfoThis = isEditingInfoId === lote.id;
                                                const isAdjustingThis = adjustingLoteId === lote.id;

                                                return (
                                                    <React.Fragment key={lote.id}>
                                                    <tr className={`lote-row ${isDepleted && !isAdjustingThis && !isEditingInfoThis ? 'lote-depleted' : ''}`}>
                                                        <td className="lote-num">
                                                            {isEditingInfoThis ? (
                                                                <input 
                                                                    type="text" 
                                                                    value={editInfoForm.numero_lote} 
                                                                    onChange={e => setEditInfoForm({...editInfoForm, numero_lote: e.target.value})}
                                                                    style={{ width: '120px', padding: '4px' }}
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <strong>{lote.numero_lote}</strong>
                                                            )}
                                                        </td>
                                                        <td className="lote-date">
                                                            {isEditingInfoThis ? (
                                                                <input 
                                                                    type="date" 
                                                                    value={editInfoForm.fecha_vencimiento} 
                                                                    onChange={e => setEditInfoForm({...editInfoForm, fecha_vencimiento: e.target.value})}
                                                                    style={{ width: '130px', padding: '4px' }}
                                                                />
                                                            ) : (
                                                                lote.fecha_vencimiento
                                                                    ? (() => {
                                                                        const fechaStr = String(lote.fecha_vencimiento).substring(0, 10);
                                                                        const d = new Date(fechaStr + 'T00:00:00');
                                                                        return isNaN(d.getTime())
                                                                            ? <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin fecha</span>
                                                                            : d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
                                                                    })()
                                                                    : <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin fecha</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`expiry-badge ${status.class}`}>
                                                                <span className="dot">{status.icon}</span>
                                                                {status.label}
                                                            </span>
                                                        </td>
                                                        <td className="lote-stock">
                                                            <strong style={{ color: stockNum > 0 ? '#10b981' : '#94a3b8' }}>
                                                                {stockNum.toFixed(2)} {product.unit || 'uds'}
                                                            </strong>
                                                        </td>
                                                        <td className="lote-ubicacion" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                            {lote.ubicacion || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>—</span>}
                                                        </td>
                                                        <td className="lote-sucursal">
                                                            {lote.sucursal_nombre || 'Principal / Global'}
                                                        </td>
                                                        {canAdjust && (
                                                            <td style={{ display: 'flex', gap: '5px' }}>
                                                                {isEditingInfoThis ? (
                                                                    <button className="btn-icon" onClick={() => handleSaveInfo(lote.id)} disabled={isSavingInfo} style={{ backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '0.8rem' }}>✔️ Guardar</button>
                                                                ) : (
                                                                    <button className="btn-icon" onClick={() => {
                                                                        setIsEditingInfoId(lote.id);
                                                                        setAdjustingLoteId(null);
                                                                        setEditInfoForm({ 
                                                                            numero_lote: lote.numero_lote || '', 
                                                                            fecha_vencimiento: lote.fecha_vencimiento ? String(lote.fecha_vencimiento).substring(0, 10) : '' 
                                                                        });
                                                                    }} title="Editar Nombre/Fecha" style={{ padding: '4px 8px', fontSize: '0.8rem', backgroundColor: 'transparent', border: 'none', borderRadius: '4px' }}>✏️ Info</button>
                                                                )}
                                                                
                                                                {!isEditingInfoThis && (
                                                                    <button 
                                                                        className="btn-icon" 
                                                                        onClick={() => {
                                                                            if (isAdjustingThis) {
                                                                                setAdjustingLoteId(null);
                                                                            } else {
                                                                                setAdjustingLoteId(lote.id);
                                                                                setIsEditingInfoId(null);
                                                                                setAdjustForm({ cantidad: '', motivo: '', observacion: '' });
                                                                            }
                                                                        }}
                                                                        title="Ajustar Lote"
                                                                        style={{ padding: '4px 8px', fontSize: '0.8rem', backgroundColor: isAdjustingThis ? '#f1f5f9' : 'transparent', border: isAdjustingThis ? '1px solid #cbd5e1' : 'none', borderRadius: '4px' }}
                                                                    >
                                                                        {isAdjustingThis ? '❌' : '✏️ Ajustar'}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        )}
                                                    </tr>
                                                    {isAdjustingThis && (
                                                        <tr className="lote-adjust-row">
                                                            <td colSpan={canAdjust ? 6 : 5} style={{ padding: 0, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                                <form onSubmit={(e) => handleAdjustLote(e, lote.id)} style={{ padding: '12px 16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                    <div className="lote-form-group" style={{ flex: '1', minWidth: '150px' }}>
                                                                        <label style={{ fontSize: '0.75rem', marginBottom: '4px' }}>Nueva Cantidad (Stock final) *</label>
                                                                        <input 
                                                                            type="number" 
                                                                            step="0.01" 
                                                                            min="0"
                                                                            value={adjustForm.cantidad} 
                                                                            onChange={e => setAdjustForm({...adjustForm, cantidad: e.target.value})}
                                                                            placeholder="Ej: 0"
                                                                            required
                                                                            style={{ padding: '6px 10px' }}
                                                                        />
                                                                    </div>
                                                                    <div className="lote-form-group" style={{ flex: '1.5', minWidth: '150px' }}>
                                                                        <label style={{ fontSize: '0.75rem', marginBottom: '4px' }}>Motivo *</label>
                                                                        <select 
                                                                            value={adjustForm.motivo}
                                                                            onChange={e => setAdjustForm({...adjustForm, motivo: e.target.value})}
                                                                            required
                                                                            style={{ padding: '6px 10px', width: '100%', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                                                                        >
                                                                            <option value="">Seleccionar...</option>
                                                                            <option value="ERROR_CONTEO">Error de registro / Conteo</option>
                                                                            <option value="DANADO">Merma / Producto dañado</option>
                                                                            <option value="VENCIMIENTO">Vencimiento no vendido</option>
                                                                            <option value="OTRO">Otro ajuste manual</option>
                                                                        </select>
                                                                    </div>
                                                                    <div className="lote-form-group" style={{ flex: '2', minWidth: '200px' }}>
                                                                        <label style={{ fontSize: '0.75rem', marginBottom: '4px' }}>Observación (Opcional)</label>
                                                                        <input 
                                                                            type="text" 
                                                                            value={adjustForm.observacion} 
                                                                            onChange={e => setAdjustForm({...adjustForm, observacion: e.target.value})}
                                                                            placeholder="Detalle extra"
                                                                            style={{ padding: '6px 10px' }}
                                                                        />
                                                                    </div>
                                                                    <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
                                                                        <button type="submit" disabled={isAdjusting} className="btn-save" style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
                                                                            {isAdjusting ? 'Guardando...' : 'Confirmar Ajuste'}
                                                                        </button>
                                                                    </div>
                                                                </form>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === 'new' && (
                        <form onSubmit={handleCreateLote} className="lote-new-form">
                            <div className="form-info-alert">
                                ℹ️ Al recepcionar mercadería, el stock se sumará de forma atómica tanto al lote como al stock general de la farmacia.
                            </div>

                            <div className="lote-form-grid">
                                <div className="lote-form-group">
                                    <label>N° de Lote del Fabricante *</label>
                                    <input 
                                        type="text"
                                        placeholder="Ej: LOTE-A4092, LT-2026-X..."
                                        value={newLote.numero_lote}
                                        onChange={(e) => setNewLote({ ...newLote, numero_lote: e.target.value })}
                                        required
                                        autoFocus
                                    />
                                    <small className="field-hint">Impreso en la caja o blíster del medicamento</small>
                                </div>

                                <div className="lote-form-group">
                                    <label>Fecha de Vencimiento / Expiración *</label>
                                    <input 
                                        type="date"
                                        value={newLote.fecha_vencimiento}
                                        onChange={(e) => setNewLote({ ...newLote, fecha_vencimiento: e.target.value })}
                                        required
                                    />
                                    <small className="field-hint">Requerido por DIGEMID para el semáforo de dispensación</small>
                                </div>

                                <div className="lote-form-group">
                                    <label>Cantidad Recibida ({product.unit || 'Unidades'}) *</label>
                                    <input 
                                        type="number"
                                        step="0.01"
                                        placeholder="0"
                                        value={newLote.cantidad}
                                        onChange={(e) => setNewLote({ ...newLote, cantidad: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="lote-form-group">
                                    <label>Ubicación en Almacén (Opcional)</label>
                                    <input 
                                        type="text"
                                        placeholder="Ej: Anaquel 1 - A, Refrigerador 2, Bodega..."
                                        value={newLote.ubicacion}
                                        onChange={(e) => setNewLote({ ...newLote, ubicacion: e.target.value })}
                                    />
                                    <small className="field-hint">Ubicación física dentro de la farmacia</small>
                                </div>

                                <div className="lote-form-group span-2">
                                    <label>Observaciones / Factura de Proveedor</label>
                                    <input 
                                        type="text"
                                        placeholder="Ej: Factura F001-4920 de Droguería Hersil"
                                        value={newLote.observacion}
                                        onChange={(e) => setNewLote({ ...newLote, observacion: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="lote-form-actions">
                                <button 
                                    type="button" 
                                    className="btn-lote-cancel"
                                    onClick={() => setActiveTab('list')}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn-lote-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Guardando Recepción...' : '✓ Confirmar Ingreso de Lote'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LotesModal;
